/**
 * `useTemplateUilmDiff` — fetches a system template's local key plan and
 * its current state on UILM, then surfaces a four-way diff to drive the
 * Translations Tab stats.
 *
 * The categories returned:
 *   - `missing`     keys we expect locally but UILM does NOT have yet
 *   - `mismatched`  keys present on both sides, EN value differs
 *   - `orphans`     keys on UILM whose owner (template/screen/field) was
 *                   deleted or never existed locally
 *   - `inSync`      keys present on both sides with matching values
 *
 * The hook handles its own discovery: it calls `getOrCreateTemplateModule`
 * which is idempotent — for a legacy template that has never been seeded,
 * the first call creates the module. Subsequent diffs reuse it.
 */

import { useQuery } from '@tanstack/react-query';
import { getTemplateByIdWithScreensAndFields } from '@/modules/templates/services/templates.service';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';
import { buildTemplateKeys, type KeyPlanEntry } from '../lib/build-template-keys';
import { DEFAULT_LANGUAGE_CULTURE } from '../lib/uilm-constants';
import { getOrCreateTemplateModule } from '../lib/uilm-module-discovery';
import {
  getUilmKeysByModule,
  type UilmKey,
} from '../services/uilm-admin.service';

export interface TemplateUilmDiff {
  moduleId: string;
  /** Full template tree as it stands locally. Used by mismatch reconciliation
   * (e.g. when "Keep UILM" on an option needs the parent field's options array). */
  template: TemplateWithScreens;
  localPlan: KeyPlanEntry[];
  remoteKeys: UilmKey[];
  missing: KeyPlanEntry[];
  mismatched: Array<{ plan: KeyPlanEntry; remote: UilmKey; remoteEn: string }>;
  orphans: UilmKey[];
  inSync: KeyPlanEntry[];
}

const QUERY_KEY = ['template-uilm-diff'];

export const useTemplateUilmDiff = (templateId: string, enabled = true) => {
  return useQuery<TemplateUilmDiff>({
    queryKey: [...QUERY_KEY, templateId],
    queryFn: () => computeDiff(templateId),
    enabled: Boolean(templateId) && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
};

async function computeDiff(templateId: string): Promise<TemplateUilmDiff> {
  const template = await getTemplateByIdWithScreensAndFields(templateId);
  if (!template) throw new Error(`Template ${templateId} not found`);
  if (template.organizationId) {
    throw new Error('UILM diff is only available for system templates');
  }

  const moduleId = await getOrCreateTemplateModule(template.slug);

  const localPlan = buildTemplateKeys({
    template,
    screens: template.screens,
  });
  const remoteKeys = await getUilmKeysByModule(moduleId);

  const remoteByName = new Map<string, UilmKey>();
  for (const k of remoteKeys) remoteByName.set(k.keyName, k);

  const localByName = new Map<string, KeyPlanEntry>();
  for (const p of localPlan) localByName.set(p.keyName, p);

  const missing: KeyPlanEntry[] = [];
  const mismatched: TemplateUilmDiff['mismatched'] = [];
  const inSync: KeyPlanEntry[] = [];

  for (const plan of localPlan) {
    const remote = remoteByName.get(plan.keyName);
    if (!remote) {
      missing.push(plan);
      continue;
    }
    const remoteEn = pickEnValue(remote);
    if (remoteEn && remoteEn !== plan.enValue) {
      mismatched.push({ plan, remote, remoteEn });
    } else {
      inSync.push(plan);
    }
  }

  const orphans = remoteKeys.filter((k) => !localByName.has(k.keyName));

  return {
    moduleId,
    template,
    localPlan,
    remoteKeys,
    missing,
    mismatched,
    orphans,
    inSync,
  };
}

/**
 * Pull the en-US resource value from a UILM key. Treat empty strings as
 * "no value" so a partially-translated key (en blank, de filled) is not
 * mistakenly flagged as a mismatch.
 */
function pickEnValue(key: UilmKey): string {
  const en = key.resources.find((r) => r.culture === DEFAULT_LANGUAGE_CULTURE);
  return en?.value ?? '';
}
